import React, { RefObject, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { GridCol, GridRow } from '@naf/grid';
import { Button } from '@naf-simba/elements-buttons';
import { Close } from '@styled-icons/material/Close';
import { Text, TextVariant } from '@naf/text';
import { Loader } from '@naf-simba/building-blocks-loaders';
import { Chip } from '@naf-simba/elements-chips';
import { ArrowUpward } from '@naf-simba/building-blocks-icons/Arrows';
import isEqual from 'lodash/isEqual';
import { updateUrlQueryValues } from '../../utils/updateUrlQueryValues';
import { actions } from '../../redux/modules/user';
import { actions as siteSearchActions } from '../../redux/modules/internalSiteSearch';
import FinderIllustration from '../icons/DynamicallyImportedFinderIllustration';
import { useScrollBlock, enableScroll } from '../../hooks/useScrollBlock';
import { SearchResultCard } from '../search-result-cards/SearchResultCard';
import useSelector from '../../redux/typedHooks';
import { SearchResultListItemLoader } from '../skeleton-loaders/SearchResultListItemLoader';
import { getPlaceholderList } from '../../utils/getPlaceholderList';
import { SearchInformationBox } from './SearchInformationBox/SearchInformationBox';
import * as S from './SearchOverlay.styles';

interface SearchOverlayProps {
  navBarRef: RefObject<HTMLDivElement>;
  isInModal?: boolean;
  onCloseResults?: () => void;
}

const SearchOverlay = ({ navBarRef, isInModal = false, onCloseResults }: SearchOverlayProps) => {
  const dispatch = useDispatch();

  const searchInputText = useSelector((state) => state.internalSiteSearch.submittedQuery);
  const searchData = useSelector((state) => state.internalSiteSearch.pages, isEqual);
  const searchError = useSelector((state) => state.internalSiteSearch.error);
  const isSearchLoading = useSelector((state) => state.internalSiteSearch.isLoading);
  const displayCount = useSelector((state) => state.internalSiteSearch.displayCount);
  const totalResults = useSelector((state) => state.internalSiteSearch.totalResults);
  const searchInformationBox = useSelector((state) => state.internalSiteSearch.searchInformationBox);

  const hasSearched = useMemo(() => Object.values(searchData).length >= 1, [searchData]);
  const pagesArray = useMemo(() => Object.entries(searchData), [searchData]);

  useScrollBlock();

  const onSearchOverlayKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCloseOverlay();
    }
  };

  const handleCloseOverlay = () => {
    if (onCloseResults) {
      onCloseResults();
      return false;
    }
    dispatch(actions.toggleSearchOverlay(false));
    dispatch(actions.toggleSearchActive(false));
    updateUrlQueryValues.updateSearchQuery('');
    return false;
  };

  const handleCtaClick = () => {
    dispatch(actions.toggleSearchOverlay(false));
    dispatch(actions.toggleSearchActive(false));
    dispatch(actions.closeMenu());
    updateUrlQueryValues.updateSearchQuery('');
    enableScroll();
  };

  const topResultElement = useRef<HTMLDivElement>(null);

  const scrollToTopResult = () => {
    topResultElement.current?.scrollIntoView();
    navBarRef.current?.scrollIntoView();
  };

  const closeButton = (
    <S.CloseButton type="button" aria-label="Lukk søkeresultater" onClick={() => handleCloseOverlay()}>
      <Close size={24} aria-hidden="true" focusable="false" />
    </S.CloseButton>
  );

  if (hasSearched && totalResults > 0) {
    return (
      <S.Wrapper id="site-searchResult" onKeyDown={onSearchOverlayKeyPress} $isInModal={isInModal}>
        {closeButton}
        <S.StyledGrid>
          <GridRow>
            <S.ResultGridCol
              s="12"
              m="12"
              l={isInModal ? '11' : '10'}
              xl={isInModal ? '11' : '6'}
              $isInModal={isInModal}
            >
              <div ref={topResultElement}>
                <Text tag="p" variant={TextVariant.Small}>
                  Søk etter <S.SearchWord>{searchInputText}</S.SearchWord> ga {totalResults} resultater
                </Text>
              </div>
            </S.ResultGridCol>
          </GridRow>
          {searchInformationBox && (
            <SearchInformationBox
              searchInformationBox={searchInformationBox}
              searchInputText={searchInputText}
              onCloseSearch={handleCtaClick}
              isInModal={isInModal}
            />
          )}
          <GridRow>
            <S.ResultGridCol
              s="12"
              m="12"
              l={isInModal ? '12' : '10'}
              xl={isInModal ? '12' : '6'}
              $isInModal={isInModal}
            >
              <S.ResultWrapper>
                {pagesArray.map(([pageNumber, page]) => {
                  if (page.meta.isLoading) {
                    return getPlaceholderList(20).map((i) => <SearchResultListItemLoader key={`${pageNumber}-${i}`} />);
                  }
                  return page.data.map((item) => <SearchResultCard key={item.id.raw} item={item} />);
                })}
              </S.ResultWrapper>
            </S.ResultGridCol>
          </GridRow>
          <S.BottomGridRow $isInModal={isInModal}>
            <S.ShowMoreGridCol
              s="12"
              m="12"
              l={isInModal ? '12' : '6'}
              xl={isInModal ? '12' : '3'}
              $isInModal={isInModal}
            >
              <S.ShowMoreButtonWrapper>
                {displayCount < totalResults && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      dispatch(siteSearchActions.getMoreSearchResults.request());
                    }}
                    disabled={isSearchLoading}
                  >
                    Vis flere
                  </Button>
                )}
                <Text tag="p" variant={TextVariant.Small}>
                  Viser {displayCount} av {totalResults}
                </Text>
              </S.ShowMoreButtonWrapper>
            </S.ShowMoreGridCol>
            <S.AnchorGridCol s="12" m="12" l={isInModal ? '12' : '4'} xl={isInModal ? '12' : '3'}>
              <S.AnchorLinks>
                <Chip
                  label="Tilbake til toppen"
                  variant="outlined"
                  color="primary"
                  startIcon={ArrowUpward}
                  onClick={scrollToTopResult}
                />
              </S.AnchorLinks>
            </S.AnchorGridCol>
          </S.BottomGridRow>
        </S.StyledGrid>
      </S.Wrapper>
    );
  }
  if (!isSearchLoading && searchError) {
    return (
      <S.Wrapper id="site-searchResult" onKeyDown={onSearchOverlayKeyPress} $isInModal={isInModal}>
        <S.StyledGrid>
          <GridRow>
            <GridCol s="12" m="12" l="12" xl="12">
              <S.ErrorWrapper>
                <FinderIllustration />
                <Text variant={TextVariant.Header3} tag="h2">
                  Ooops, noe gikk dessverre galt
                </Text>
                <Text tag="p" variant={TextVariant.ArticleText}>
                  Vi klarte ikke finne veien. Vennligst forsøk igjen senere.
                </Text>
                <Button onClick={() => handleCloseOverlay()}>Lukk søk</Button>
              </S.ErrorWrapper>
            </GridCol>
          </GridRow>
        </S.StyledGrid>
      </S.Wrapper>
    );
  }
  if (totalResults === 0) {
    return (
      <S.Wrapper id="site-searchResult" onKeyDown={onSearchOverlayKeyPress} $isInModal={isInModal}>
        {closeButton}
        <S.StyledGrid>
          {searchInformationBox ? (
            <S.NoResultGridCol s="12" m="12" l="12" xl="12">
              <SearchInformationBox
                searchInformationBox={searchInformationBox}
                searchInputText={searchInputText}
                onCloseSearch={handleCtaClick}
                isInModal={isInModal}
              />
            </S.NoResultGridCol>
          ) : (
            <GridRow>
              <GridCol s="12" m="12" l="12" xl="12">
                <S.ErrorWrapper>
                  <FinderIllustration />
                  <Text variant={TextVariant.Header3} tag="h2">
                    Vi fant dessverre ingen treff på {`"${searchInputText}"`}
                  </Text>
                  <Text tag="p" variant={TextVariant.ArticleText}>
                    Prøv igjen senere eller søk etter noe annet.
                  </Text>
                </S.ErrorWrapper>
              </GridCol>
            </GridRow>
          )}
        </S.StyledGrid>
      </S.Wrapper>
    );
  }
  return (
    <S.Wrapper id="site-searchResult" onKeyDown={onSearchOverlayKeyPress} $isInModal={isInModal}>
      <S.StyledGrid>
        <GridRow>
          <GridCol s="12" m="12" l="12" xl="12">
            <S.ErrorWrapper>
              <Text variant={TextVariant.Header3}>Søker ...</Text>
              <Loader />
            </S.ErrorWrapper>
          </GridCol>
        </GridRow>
      </S.StyledGrid>
    </S.Wrapper>
  );
};

export default SearchOverlay;
